import { useUser } from '../contexts/UserContext';

/**
 * Hook for checking user permissions and roles
 * Provides a cleaner API for permission-based UI rendering
 *
 * @example
 * const { can, canAny, canAll, is, isAny } = usePermission();
 *
 * // Check single permission
 * if (can('claims:view_all')) { ... }
 *
 * // Check any of multiple permissions
 * if (canAny(['claims:view_all', 'claims:view_assigned'])) { ... }
 *
 * // Check all permissions
 * if (canAll(['claims:edit', 'claims:change_status'])) { ... }
 *
 * // Check role
 * if (is('admin')) { ... }
 *
 * // Check any role
 * if (isAny(['admin', 'claims_admin'])) { ... }
 */
const usePermission = () => {
  const {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    roles,
    permissions,
    user,
  } = useUser();

  return {
    // Permission checks with shorter names
    can: hasPermission,
    canAny: hasAnyPermission,
    canAll: hasAllPermissions,

    // Role checks with shorter names
    is: hasRole,
    isAny: hasAnyRole,

    // Raw arrays for advanced use cases
    roles,
    permissions,

    // User object for fallback checks during migration
    user,
  };
};

export default usePermission;
